import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MenuBar from "../components/MenuBar";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import { useSalesImportBranchFilter } from "../features/salesDataImport/hooks/useSalesImportBranchFilter";
import {
  fetchBranchMonthlySalesPurchaseReport,
  flattenBranchMonthlyForExport,
} from "../features/salesDataImport/services/branchMonthlyTradeService";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function BranchMonthlySalesPurchasePage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const nowYear = new Date().getFullYear();
  const { branchId, branchFilterLabel } = useSalesImportBranchFilter();
  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [year, setYear] = useState(nowYear);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");

  const yearOptions = useMemo(() => [nowYear - 1, nowYear, nowYear + 1], [nowYear]);

  const filteredBranches = useMemo(() => {
    let list = report?.branches || [];
    if (branchId !== "all") {
      list = list.filter((r) => String(r.branch) === String(branchId));
    }
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (r) =>
        String(r.branch || "").toLowerCase().includes(term) ||
        String(r.branchLabel || "").toLowerCase().includes(term)
    );
  }, [report, search, branchId]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setProgress(0);
    try {
      const out = await fetchBranchMonthlySalesPurchaseReport(year, (n) => setProgress(n));
      setReport(out);
      if (!out.branches.length) {
        toast.error("No lines with TranDate and BranchID for this year. Upload sales import data first.");
      } else {
        toast.success(
          `Loaded ${out.lineCount.toLocaleString()} lines · ${out.branches.length} branch(es)`
        );
      }
    } catch (e) {
      toast.error(formatFirebaseError(e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    loadReport();
  }, [user, loadReport]);

  useEffect(() => {
    localStorage.setItem("sdic-dark", dark ? "1" : "0");
  }, [dark]);

  const exportExcel = () => {
    if (!report?.branches?.length) {
      toast.error("Nothing to export.");
      return;
    }
    const rows = flattenBranchMonthlyForExport(report);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BranchMonthly");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `branch-monthly-sales-purchase-${year}.xlsx`
    );
  };

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>Branch Monthly Sales &amp; Purchase</h1>
          <p>
            {branchFilterLabel} · Sales (non–Inward Supply) and purchase (Inward Supply) by{" "}
            <strong>BranchID</strong> from
            uploaded sales import line chunks
          </p>
        </div>
        <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
          {dark ? "Light" : "Dark"}
        </button>
      </header>

      <div className="sdic-body">
        <SalesImportNav active="branch-monthly" />

        <section className="sdic-panel">
          <div className="sdic-week-banner" style={{ flexWrap: "wrap" }}>
            <label>
              Year
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                style={{ padding: "0.4rem", minWidth: "100px" }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="sdic-btn sdic-btn-primary"
              disabled={loading}
              onClick={loadReport}
            >
              {loading ? `Loading… ${progress.toLocaleString()} lines` : "Refresh"}
            </button>
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              disabled={!report?.branches?.length}
              onClick={exportExcel}
            >
              Export Excel
            </button>
            <input
              className="sdic-search"
              style={{ margin: 0, maxWidth: 220 }}
              placeholder="Search branch…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <p style={{ fontSize: "0.83rem", color: "var(--sdic-muted)", margin: "0.5rem 0 0" }}>
            Sales = all voucher types except <strong>Inward Supply</strong> (uses line{" "}
            <strong>Amount</strong>). Purchase = <strong>Inward Supply</strong> only. Month from{" "}
            <strong>Trans Date</strong>.
          </p>
        </section>

        {report && (
          <>
            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Year {year} totals</h2>
              <div className="sdic-validation-grid">
                <div className="sdic-val-box sdic-val-valid">
                  Total sales
                  <br />₹ {fm(report.grandSales)}
                </div>
                <div className="sdic-val-box" style={{ borderColor: "var(--sdic-accent)" }}>
                  Total purchase
                  <br />₹ {fm(report.grandPurchase)}
                </div>
                <div className="sdic-val-box sdic-val-dup">
                  Net (Sales − Purchase)
                  <br />₹ {fm(report.grandNet)}
                </div>
                <div className="sdic-val-box">
                  Branches
                  <br />
                  {report.branches.length}
                </div>
              </div>
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">By branch and month</h2>
              <div className="sdic-table-wrap" style={{ maxHeight: "70vh" }}>
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ position: "sticky", left: 0, zIndex: 2, background: "inherit" }}>
                        Branch
                      </th>
                      {(report.months || []).map((m) => (
                        <th key={m.monthKey} colSpan={2} style={{ textAlign: "center" }}>
                          {m.label}
                        </th>
                      ))}
                      <th colSpan={2} style={{ textAlign: "center" }}>
                        Year total
                      </th>
                    </tr>
                    <tr>
                      {(report.months || []).map((m) => (
                        <React.Fragment key={`${m.monthKey}-sub`}>
                          <th style={{ fontSize: "0.75rem" }}>Sales</th>
                          <th style={{ fontSize: "0.75rem" }}>Purchase</th>
                        </React.Fragment>
                      ))}
                      <th style={{ fontSize: "0.75rem" }}>Sales</th>
                      <th style={{ fontSize: "0.75rem" }}>Purchase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBranches.map((br) => (
                      <tr key={br.branch}>
                        <td style={{ position: "sticky", left: 0, fontWeight: 600, background: "inherit" }}>
                          {br.branchLabel || br.branch}
                        </td>
                        {(br.months || []).map((m) => (
                          <React.Fragment key={`${br.branch}-${m.monthKey}`}>
                            <td>₹ {fm(m.salesAmount)}</td>
                            <td>₹ {fm(m.purchaseAmount)}</td>
                          </React.Fragment>
                        ))}
                        <td>
                          <strong>₹ {fm(br.yearSales)}</strong>
                        </td>
                        <td>
                          <strong>₹ {fm(br.yearPurchase)}</strong>
                        </td>
                      </tr>
                    ))}
                    {!filteredBranches.length && (
                      <tr>
                        <td colSpan={(report.months?.length || 0) * 2 + 3} style={{ color: "var(--sdic-muted)" }}>
                          No branches match your search.
                        </td>
                      </tr>
                    )}
                    {!!filteredBranches.length && (
                      <tr style={{ fontWeight: 700, background: "var(--sdic-bg-soft, rgba(0,0,0,0.04))" }}>
                        <td style={{ position: "sticky", left: 0, background: "inherit" }}>
                          All branches
                        </td>
                        {(report.monthTotals || []).map((m) => (
                          <React.Fragment key={`tot-${m.monthKey}`}>
                            <td>₹ {fm(m.salesAmount)}</td>
                            <td>₹ {fm(m.purchaseAmount)}</td>
                          </React.Fragment>
                        ))}
                        <td>₹ {fm(report.grandSales)}</td>
                        <td>₹ {fm(report.grandPurchase)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {!loading && !report?.branches?.length && (
          <p style={{ color: "var(--sdic-muted)" }}>
            No data for {year}. Upload weekly files in{" "}
            <Link to="/sales-data-import">Sales Data Import Center</Link> with BranchID and Trans Date
            columns.
          </p>
        )}
      </div>
    </div>
  );
}
